/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Collapse,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import AppLayout from "layout/AppLayout";
import axios from "axiosClient";
import Pagination from "containers/pages/Pagination";
import SelectInput from "components/common/SelectInput";
import {
  BridgeError,
  ackJournalEntry,
  printReceipt,
  sendCommand,
} from "utils/posBridge";
import { buildPrintPayload } from "utils/posReceiptPrint";
import { explainTerminalError } from "./terminalErrors";
import { SaleStatusBadge, money } from "./shared";

const PosSalesList = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [filters, setFilters] = useState({ search: "", status: "", from: "", to: "" });
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // the filters behind the rows on screen, for the empty message and Retry
  const [shownFilters, setShownFilters] = useState(filters);

  /** Reprints the receipt stored against a sale. */
  const reprint = async (posSale) => {
    if (!posSale.receipt) {
      return toast.error("This sale was registered before receipts were stored");
    }
    setPrintingId(posSale._id);
    try {
      await printReceipt(await buildPrintPayload(posSale.receipt));
      toast.success("Receipt sent to the printer");
      (posSale.paymentIds || []).forEach((paymentId) =>
        axios
          .post(`/api/payments/${paymentId}/receipt-event`, {
            event: "printed",
            source: "pos-sales",
          })
          .catch(() => {}),
      );
    } catch (error) {
      toast.error(error.message || "Could not print the receipt");
    } finally {
      setPrintingId(null);
    }
  };

  const load = useCallback(
    (nextPage = 1, values = filters) => {
      setLoading(true);
      setLoadError("");
      setShownFilters(values);
      axios
        .post("/api/pos/sales", { page: nextPage, per_page: 30, ...values })
        .then(({ data }) => {
          setItems(data.items);
          setTotal(data.total);
          setPage(data.page);
          setPageCount(data.pageCount);
        })
        .catch(() => {
          toast.error("Could not load POS sales");
          setLoadError(
            "Could not load POS sales. Check your connection and try again.",
          );
        })
        .finally(() => setLoading(false));
    },
    [filters],
  );

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Refunds run the same prepare → terminal → record cycle as a sale. The
   * terminal will also try to reverse the original card transaction when the
   * amount matches and the day is not yet settled.
   */
  const refund = async (posSale) => {
    // Anything not taken on this terminal cannot be reversed from it: the refund
    // here is fiscal only, and the money has to go back where it came from.
    const outside = (posSale.payments || [])
      .map((tender, index) => ({
        tender,
        meta: posSale.tenderMeta?.[index] || {},
      }))
      .filter(({ tender }) => tender.paymentType === "Cashless");
    const outsideNote = outside.length
      ? `\n\n${outside
          .map(({ tender, meta }) =>
            meta.cardBrand
              ? `${money(tender.amount)} was taken on another terminal (${meta.cardBrand}${
                  meta.externalReference ? `, ref ${meta.externalReference}` : ""
                }).`
              : `${money(tender.amount)} was paid by bank transfer.`,
          )
          .join(
            "\n",
          )}\nThis records the refund for the tax service only - return that money through the terminal or bank it was paid on.`
      : "";

    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Refund ${money(posSale.totalAmount)} to ${posSale.candidate?.fullName || "this candidate"}?${outsideNote}`,
      )
    )
      return;

    setBusy(true);
    let prepared;
    try {
      const { data } = await axios.post(`/api/pos/sale/${posSale._id}/refund/prepare`);
      prepared = data;
    } catch (error) {
      setBusy(false);
      return toast.error(error.response?.data?.message || "Could not prepare the refund");
    }

    try {
      const result = await sendCommand({
        id: prepared.requestId,
        command: "Refund",
        data: prepared.data,
      });
      await axios.post(`/api/pos/sale/${posSale._id}/refund/result`, {
        requestId: prepared.requestId,
        response: result.response,
      });
      await ackJournalEntry(prepared.requestId).catch(() => {});

      if (result.response.status === "Success") {
        toast.success("Refund registered");
        if (result.response.error) {
          toast.warn(explainTerminalError(result.response.error));
        }
      } else {
        toast.error(explainTerminalError(result.response.error));
      }
    } catch (error) {
      if (error instanceof BridgeError) {
        toast.error(error.message);
        if (error.retryableWithSameId) {
          toast.info(
            "The terminal may still have processed this refund — check before retrying.",
            { autoClose: 8000 },
          );
        }
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setBusy(false);
      load(page);
    }
  };

  const hasFilters = Object.values(shownFilters || {}).some(Boolean);

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <Card className="h-100 m-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <CardTitle>POS sales</CardTitle>
              <NavLink to="/app/pos" className="btn btn-light">
                Back to till
              </NavLink>
            </div>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!loading) load(1, filters);
              }}
            >
              <Row className="text-left">
                <Col md="4">
                  <FormGroup>
                    <Label>Candidate, passport, sale or enquiry id</Label>
                    <Input
                      value={filters.search}
                      onChange={(e) =>
                        setFilters({ ...filters, search: e.target.value })
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Status</Label>
                    <SelectInput
                      placeholder="All"
                      isClearable
                      value={filters.status}
                      onChange={(status) => setFilters({ ...filters, status })}
                      options={[
                        { label: "Registered", value: "registered" },
                        { label: "Awaiting result", value: "pending" },
                        { label: "Failed", value: "failed" },
                      ]}
                    />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <Label>From</Label>
                    <Input
                      type="date"
                      value={filters.from}
                      onChange={(e) =>
                        setFilters({ ...filters, from: e.target.value })
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <Label>To</Label>
                    <Input
                      type="date"
                      value={filters.to}
                      onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md="1" className="d-flex align-items-end mb-3">
                  <Button
                    color="primary"
                    onClick={() => load(1, filters)}
                    disabled={loading}
                  >
                    Search
                  </Button>
                </Col>
              </Row>
            </Form>

            <p className="text-muted text-small">
              {loading ? "Loading…" : `${total} sale(s)`}
            </p>

            <Table responsive className="r-table table text-left">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Candidate</th>
                  <th>Exam</th>
                  <th className="money-cell">Total</th>
                  <th>Payment</th>
                  <th>Sale ID</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loadError && (
                  <tr>
                    <td colSpan="8" className="text-danger">
                      {loadError}
                      <Button
                        color="danger"
                        outline
                        size="xs"
                        className="ml-3"
                        disabled={loading}
                        onClick={() => load(page, shownFilters)}
                      >
                        Retry
                      </Button>
                    </td>
                  </tr>
                )}
                {!loadError && loading && items.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-muted">
                      Loading sales…
                    </td>
                  </tr>
                )}
                {!loadError && !loading && items.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-muted">
                      {hasFilters
                        ? "No sales match these filters."
                        : "No POS sales yet."}
                    </td>
                  </tr>
                )}
                {!loadError && items.map((item) => (
                  <React.Fragment key={item._id}>
                    <tr>
                      <td className="text-muted text-small">
                        {moment(item.createdAt).utcOffset("+05:00").format("DD/MM/YYYY HH:mm")}
                      </td>
                      <td>
                        {item.candidate?.fullName || "—"}
                        <br />
                        <span className="text-muted text-small">
                          {item.candidate?.passportId}
                        </span>
                      </td>
                      <td className="text-muted">
                        {item.exam}
                        <br />
                        <span className="text-small">{item.module}</span>
                      </td>
                      <td className="money-cell">
                        {money(item.totalAmount)}
                        {item.discountAmount > 0 && (
                          <>
                            <br />
                            <span className="text-muted text-small">
                              −{money(item.discountAmount)}
                              {item.promocode ? ` ${item.promocode}` : ""}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="text-muted text-small">
                        {(item.payments || [])
                          .map((p) => `${p.paymentType} ${money(p.amount)}`)
                          .join(", ")}
                      </td>
                      <td className="text-muted text-small">{item.saleId || "—"}</td>
                      <td>
                        <SaleStatusBadge
                          status={item.status}
                          refundedAmount={item.refundedAmount}
                        />
                        {item.terminalError && (
                          <div className="text-muted text-small">
                            {explainTerminalError(item.terminalError)}
                          </div>
                        )}
                      </td>
                      <td className="pos-row-actions">
                        <button
                          type="button"
                          className={`pos-icon-action${
                            expanded === item._id ? " is-active" : ""
                          }`}
                          title={
                            expanded === item._id ? "Hide details" : "Details"
                          }
                          aria-label="Details"
                          aria-expanded={expanded === item._id}
                          onClick={() =>
                            setExpanded(expanded === item._id ? null : item._id)
                          }
                        >
                          <i
                            className={
                              expanded === item._id
                                ? "simple-icon-arrow-up"
                                : "simple-icon-info"
                            }
                          />
                        </button>
                        {item.status === "registered" && (
                          <button
                            type="button"
                            className={`pos-icon-action${
                              printingId === item._id ? " is-busy" : ""
                            }`}
                            title={
                              printingId === item._id
                                ? "Printing…"
                                : "Print receipt"
                            }
                            aria-label="Print receipt"
                            disabled={printingId === item._id}
                            onClick={() => reprint(item)}
                          >
                            <i className="simple-icon-printer" />
                          </button>
                        )}
                        {item.status === "registered" && !item.refundedAmount && (
                          <button
                            type="button"
                            className="pos-icon-action is-danger"
                            title="Refund"
                            aria-label="Refund"
                            disabled={busy}
                            onClick={() => refund(item)}
                          >
                            <i className="simple-icon-action-undo" />
                          </button>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="8" className="p-0 border-0">
                        <Collapse isOpen={expanded === item._id}>
                          <div className="p-3 text-small">
                            <Row>
                              <Col md="6">
                                <p className="mb-1 text-muted">Request id</p>
                                <p>{item.requestId}</p>
                                <p className="mb-1 text-muted">Cashier</p>
                                <p>
                                  {item.createdBy?.username || "—"}
                                  {item.terminalUser ? ` · shift ${item.terminalUser}` : ""}
                                </p>
                              </Col>
                              <Col md="6">
                                {item.saleReceipt?.fSign && (
                                  <>
                                    <p className="mb-1 text-muted">Fiscal sign</p>
                                    <p>{item.saleReceipt.fSign}</p>
                                  </>
                                )}
                                {item.saleReceipt?.qr && (
                                  <p>
                                    <a
                                      href={item.saleReceipt.qr}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Fiscal receipt on ofd.soliq.uz
                                    </a>
                                  </p>
                                )}
                                {item.skPayReceipt?.rrn && (
                                  <>
                                    <p className="mb-1 text-muted">Card RRN</p>
                                    <p>
                                      {item.skPayReceipt.rrn} ·{" "}
                                      {item.skPayReceipt.cardMask}
                                    </p>
                                  </>
                                )}
                              </Col>
                              {item.refunds?.length > 0 && (
                                <Col md="12">
                                  <p className="mb-1 text-muted">Refunds</p>
                                  {item.refunds.map((r) => (
                                    <div key={r.requestId}>
                                      {money(r.amount)} · {r.status}
                                      {r.terminalError
                                        ? ` · ${explainTerminalError(r.terminalError)}`
                                        : ""}
                                    </div>
                                  ))}
                                </Col>
                              )}
                            </Row>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>

            <Pagination
              currentPage={page}
              totalPage={pageCount}
              onChangePage={(next) => load(next, filters)}
            />
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  );
};

export default PosSalesList;
